import styled from 'styled-components'

import type { Intent } from '../theme'
import type { ClassNameProps } from '@root/shared/react'

export type ProgressLinePlacement = 'top' | 'bottom' | 'inline'

export type ProgressLineProps = ClassNameProps & {
  value: number
  startContent?: React.ReactNode
  endContent?: React.ReactNode
  percentage?: number
  intent?: Intent
  placement?: ProgressLinePlacement
  max?: number
}

export const ProgressLine: React.FC<ProgressLineProps> = ({
  value,
  startContent,
  endContent,
  intent = 'success',
  placement = 'top',
  max = 100,
  className,
}) => {
  return (
    <Root className={className} data-placement={placement}>
      <StartContent>{startContent}</StartContent>
      <Progress value={value} max={max} $intent={intent} />
      <EndContent>{endContent}</EndContent>
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  width: 100%;
  gap: 5px;
  align-items: center;

  &[data-placement='inline'] {
    grid-template-columns: auto minmax(0, 1fr) auto;
    grid-template-areas: 'start progress end';
  }

  &[data-placement='bottom'] {
    grid-template-columns: auto auto;
    grid-template-areas:
      'progress progress'
      'start end';
    justify-content: space-between;
  }

  &[data-placement='top'] {
    grid-template-columns: auto auto;
    grid-template-areas:
      'start end'
      'progress progress';
    justify-content: space-between;
  }
`

const StartContent = styled.div`
  grid-area: start;
  font-size: 10px;
  font-weight: 500;
  line-height: 12px;
  color: var(--text-11);
  text-align: end;
`

const EndContent = styled.div`
  grid-area: end;
  font-size: 10px;
  font-weight: 500;
  line-height: 12px;
  color: var(--text-11);
  text-align: start;
`

const progressValueColor: Record<Intent, string> = {
  success: 'var(--up-9)',
  error: 'var(--down-9)',
  neutral: 'var(--neutral-9)',
  warning: 'var(--warning-9)',
}

const Progress = styled.progress<{ $intent: Intent }>`
  grid-area: progress;
  width: 100%;
  height: 4px;
  appearance: none;
  -webkit-appearance: none;
  border-radius: 2px;
  overflow: hidden;

  &::-webkit-progress-bar {
    background-color: var(--progress-background, var(--background-3));
  }

  &::-webkit-progress-value {
    background-color: ${({ $intent }) => progressValueColor[$intent]};
    border-radius: 2px;
  }
`
